import './index.less';
import { uniq, startCase } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useCreateAccountProvider } from '@/lib/context';
import { HelpVideo } from '@/shared/types/help-videos.types';
import { useQueryParams } from '@/shared/hooks';
import { ParamKey } from '@/shared/types/url-params';
import { businessSettingsDisplay } from '@/lib/service';
import { BusinessSettingConfigKey } from '@/shared/types/business-setting';
import VimeoDialog from '../Common/VimeoDialog';
import classNames from 'classnames';
import Image from 'next/image';

const HelpVideos = () => {
  const { showSignupDialog } = useCreateAccountProvider();

  const { queryParams, setQueryParams } = useQueryParams<{}>({ autoScroll: false });
  const categoryParam = queryParams.get(ParamKey.CATEGORY);
  const [videos, setVideos] = useState<HelpVideo[]>([]);
  const [filters, setFilters] = useState<string[]>([]);
  const videoId = queryParams.get(ParamKey.VIDEO_ID);

  useEffect(() => {
    if (!videos.length) {
      (async () => {
        const { value } = await businessSettingsDisplay(BusinessSettingConfigKey.HELP_VIDEOS);
        setVideos(value);
        setFilters(uniq(value?.map((video: HelpVideo) => video.category)) || []);
      })();
    }
  }, [videos, setVideos]);

  const [filteredResults, setFilteredResults] = useState<HelpVideo[]>([]);

  useEffect(() => {
    if (!categoryParam) {
      setFilteredResults(videos);
      return;
    }

    setFilteredResults(videos.filter((video: HelpVideo) => video.category === categoryParam));
  }, [queryParams, categoryParam, setFilteredResults, videos]);

  const categoryTabs = (
    <div className="category-tabs">
      <span
        key="all"
        className={classNames('option', { active: categoryParam === null })}
        onClick={() => {
          setQueryParams({ [ParamKey.CATEGORY]: null });
        }}
      >
        All
      </span>
      {filters.map((item: string) => {
        return (
          <span
            key={item}
            className={classNames('option', { active: categoryParam === item })}
            onClick={() => {
              setQueryParams({ [ParamKey.CATEGORY]: item });
            }}
          >
            {startCase(item)}
          </span>
        );
      })}
    </div>
  );

  const videoGrid = (
    <div className="video-grid" key={categoryParam ?? 'All'}>
      {filteredResults.map((video: HelpVideo, index: number) => (
        <motion.div
          key={video.vimeoVideoId}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.1, ease: 'easeInOut' }}
        >
          <div
            className="help-video-card"
            onClick={() => {
              setQueryParams({ [ParamKey.VIDEO_ID]: video.vimeoVideoId.toString() });
            }}
          >
            <div className="thumbnail-container">
              <Image
                className="thumbnail"
                src={video.thumbnailUrl}
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                alt={video.title}
                loading="lazy"
              />
            </div>
            <span className="title">{video.title}</span>
            <span className="description">{video.description}</span>
          </div>
        </motion.div>
      ))}
    </div>
  );

  const content = (
    <div className="content-container">
      {categoryTabs}
      {videoGrid}
      {videoId && (
        <VimeoDialog
          key={videoId}
          visible={true}
          videoId={parseInt(videoId)}
          autoplay
          onClose={() => {
            setQueryParams({ [ParamKey.VIDEO_ID]: undefined });
          }}
        />
      )}
    </div>
  );

  return (
    <div className="help-videos">
      <div className="help-page-intro-wrap">
        <div className="padded-container">
          <div className="help-page-intro-header-wrap">
            <h1 className="title">Pietra Tutorials</h1>
            <div className="subtitle">Watch step-by-step tutorials to get the most out of your Pietra experience</div>
          </div>
          <button className="hero-btn" onClick={() => showSignupDialog()}>
            Create Account
          </button>
        </div>
      </div>

      <div className="content-body">{content}</div>
    </div>
  );
};

export default HelpVideos;
